import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars, FaBell, FaBuilding, FaChevronDown,
  FaCheck, FaCheckDouble, FaTrash, FaWhatsapp, FaExclamationTriangle,
  FaCreditCard, FaKey, FaDesktop, FaBullhorn, FaInfoCircle,
  FaCopy, FaEdit, FaTimes,
} from "react-icons/fa";
import { useNotifications } from "../context/NotificationContext";
import { useWorkspace } from "../context/WorkspaceContext";

const NOTIF_ICONS = {
  session_connected: { icon: FaWhatsapp, color: "text-green-500", bg: "bg-green-50" },
  session_disconnected: { icon: FaWhatsapp, color: "text-red-500", bg: "bg-red-50" },
  message_received: { icon: FaWhatsapp, color: "text-brand", bg: "bg-brand-50" },
  campaign_completed: { icon: FaBullhorn, color: "text-blue-500", bg: "bg-blue-50" },
  message_failed: { icon: FaExclamationTriangle, color: "text-amber-500", bg: "bg-amber-50" },
  plan_expiry: { icon: FaCreditCard, color: "text-orange-500", bg: "bg-orange-50" },
  billing: { icon: FaCreditCard, color: "text-purple-500", bg: "bg-purple-50" },
  login_alert: { icon: FaDesktop, color: "text-indigo-500", bg: "bg-indigo-50" },
  api_key_alert: { icon: FaKey, color: "text-amber-600", bg: "bg-amber-50" },
  delivery_degraded: { icon: FaExclamationTriangle, color: "text-red-500", bg: "bg-red-50" },
  session_logged_out: { icon: FaWhatsapp, color: "text-red-500", bg: "bg-red-50" },
  system: { icon: FaInfoCircle, color: "text-ink-muted", bg: "bg-surface-bg" },
};

const PAGE_TITLES = [
  ["/dashboard", "Dashboard"],
  ["/whatsapp-phones", "WhatsApp Phones"],
  ["/cloud-sessions", "Cloud API"],
  ["/unibox", "Unibox"],
  ["/campaigns", "Campaigns"],
  ["/flow-builder", "Flow Builder"],
  ["/send-via-excel", "Send via Excel"],
  ["/number-verification", "Number Verification"],
  ["/group-members-export", "Group Member Export"],
  ["/image-to-url", "Image to URL"],
  ["/whatsapp-widget", "WhatsApp Widget"],
  ["/reports", "Reports"],
  ["/settings", "Settings"],
  ["/pricing", "Plans and Billing"],
  ["/guide", "User Guide"],
  ["/admin", "Administration"],
];

function timeAgo(date) {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString();
}

export default function Header({ toggleSidebar }) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspaceCopied, setWorkspaceCopied] = useState(false);
  const [renamingWorkspace, setRenamingWorkspace] = useState(false);
  const [workspaceNameDraft, setWorkspaceNameDraft] = useState("");
  const [workspaceNameError, setWorkspaceNameError] = useState("");
  const workspaceRef = useRef(null);
  const notifRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { activeWorkspace, workspaces, switchWorkspace, renameWorkspace, switching, renaming } = useWorkspace() || {};
  const {
    notifications, unreadCount, fetchNotifications,
    markAsRead, markAllRead, deleteNotif, clearAll,
  } = useNotifications();
  const pageTitle = PAGE_TITLES.find(([path]) => location.pathname.startsWith(path))?.[1] || "Workspace";

  const toggleNotifPanel = () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening) {
      setWorkspaceOpen(false);
      fetchNotifications();
    }
  };

  const activeWorkspaceId = activeWorkspace?.clientId || activeWorkspace?.id || "";
  const canRenameActiveWorkspace = ["owner", "admin"].includes(activeWorkspace?.role);

  const copyWorkspaceId = (e) => {
    e.stopPropagation();
    if (!activeWorkspaceId) return;
    navigator.clipboard?.writeText(activeWorkspaceId);
    setWorkspaceCopied(true);
    setTimeout(() => setWorkspaceCopied(false), 2000);
  };

  const startRenameWorkspace = (e) => {
    e.stopPropagation();
    setWorkspaceNameDraft(activeWorkspace?.name || "");
    setWorkspaceNameError("");
    setRenamingWorkspace(true);
  };

  const cancelRenameWorkspace = (e) => {
    e.stopPropagation();
    setRenamingWorkspace(false);
    setWorkspaceNameDraft("");
    setWorkspaceNameError("");
  };

  const submitRenameWorkspace = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const nextName = workspaceNameDraft.trim();
    if (!nextName) {
      setWorkspaceNameError("Workspace name is required");
      return;
    }

    try {
      await renameWorkspace?.(activeWorkspace.id, nextName);
      setRenamingWorkspace(false);
      setWorkspaceNameDraft("");
      setWorkspaceNameError("");
    } catch (err) {
      setWorkspaceNameError(err.response?.data?.message || "Could not rename workspace");
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (workspaceRef.current && !workspaceRef.current.contains(e.target)) {
        setWorkspaceOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="app-header">
      {/* Left */}
      <div className="flex items-center gap-3">
        <button className="btn-icon" onClick={toggleSidebar}>
          <FaBars size={16} />
        </button>
        <div>
          <p className="app-header-context hidden sm:block">Walytic operations</p>
          <h2 className="text-sm font-bold tracking-[-0.01em] text-ink-primary">{pageTitle}</h2>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">
        <div className="relative" ref={workspaceRef}>
          <button
            type="button"
            className="flex h-9 max-w-[220px] items-center gap-2 rounded-md border border-surface-border bg-surface-card px-3 text-sm font-semibold text-ink-primary hover:bg-surface-hover"
            onClick={() => { setWorkspaceOpen((prev) => !prev); setNotifOpen(false); }}
            disabled={switching}
            title={activeWorkspace?.name || "Workspace"}
          >
            <FaBuilding size={13} className="text-brand flex-shrink-0" />
            <span className="truncate">{activeWorkspace?.name || "Workspace"}</span>
            <FaChevronDown size={10} className="text-ink-muted flex-shrink-0" />
          </button>

          {workspaceOpen && (
            <div className="absolute right-0 z-50 mt-2 w-72 rounded-md border border-surface-border bg-surface-card py-1 shadow-lg">
              {activeWorkspace && (
                <div className="px-3 py-3 border-b border-surface-border">
                  {renamingWorkspace ? (
                    <form className="space-y-1" onSubmit={submitRenameWorkspace}>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={workspaceNameDraft}
                          onChange={(e) => {
                            setWorkspaceNameDraft(e.target.value);
                            setWorkspaceNameError("");
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-8 min-w-0 flex-1 rounded-md border border-surface-border bg-surface-bg px-2 text-xs text-ink-primary outline-none focus:border-brand"
                          maxLength={100}
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="btn-secondary btn-sm btn-icon flex-shrink-0"
                          disabled={renaming}
                          title="Save workspace name"
                        >
                          <FaCheck size={10} />
                        </button>
                        <button
                          type="button"
                          className="btn-secondary btn-sm btn-icon flex-shrink-0"
                          onClick={cancelRenameWorkspace}
                          disabled={renaming}
                          title="Cancel"
                        >
                          <FaTimes size={10} />
                        </button>
                      </div>
                      {workspaceNameError && (
                        <p className="text-[11px] text-red-500">{workspaceNameError}</p>
                      )}
                    </form>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="min-w-0 flex-1 text-xs font-semibold text-ink-primary truncate">{activeWorkspace.name}</p>
                      {canRenameActiveWorkspace && (
                        <button
                          type="button"
                          className="btn-secondary btn-sm btn-icon flex-shrink-0"
                          onClick={startRenameWorkspace}
                          title="Rename workspace"
                        >
                          <FaEdit size={10} />
                        </button>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-2xs uppercase text-ink-faint">Workspace ID</p>
                      <code className="block text-[11px] text-ink-muted font-mono truncate">
                        {activeWorkspaceId}
                      </code>
                    </div>
                    <button
                      type="button"
                      className="btn-secondary btn-sm btn-icon flex-shrink-0"
                      onClick={copyWorkspaceId}
                      title="Copy workspace ID"
                    >
                      {workspaceCopied ? <FaCheck size={10} className="text-green-500" /> : <FaCopy size={10} />}
                    </button>
                  </div>
                </div>
              )}
              {(workspaces || []).map((workspace) => (
                <button
                  key={workspace.id}
                  className={`w-full text-left px-3 py-2.5 text-sm hover:bg-surface-hover flex items-center justify-between gap-3 ${
                    workspace.id === activeWorkspace?.id ? "text-brand font-semibold" : "text-ink-secondary"
                  }`}
                  onClick={() => { setWorkspaceOpen(false); switchWorkspace?.(workspace.id); }}
                >
                  <span className="truncate">{workspace.name}</span>
                  <span className="text-2xs uppercase text-ink-faint flex-shrink-0">{workspace.role}</span>
                </button>
              ))}
              {(!workspaces || workspaces.length === 0) && (
                <div className="px-3 py-2 text-sm text-ink-muted">No workspaces</div>
              )}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button className="btn-icon relative" onClick={toggleNotifPanel}>
            <FaBell size={15} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-2xs font-bold flex items-center justify-center">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications panel */}
          {notifOpen && (
            <div className="absolute right-0 z-50 mt-2 flex max-h-[480px] w-80 flex-col overflow-hidden rounded-lg border border-surface-border bg-surface-card shadow-xl sm:w-96">
              {/* Header */}
              <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-ink-primary">Notifications</h3>
                  {unreadCount > 0 && (
                    <span className="badge-brand text-2xs">{unreadCount} new</span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {unreadCount > 0 && (
                    <button
                      className="btn-ghost text-2xs px-2 py-1 flex items-center gap-1"
                      onClick={markAllRead}
                      title="Mark all as read"
                    >
                      <FaCheckDouble size={10} /> Read all
                    </button>
                  )}
                  {notifications.length > 0 && (
                    <button
                      className="btn-ghost text-2xs px-2 py-1 text-red-500 hover:text-red-600 flex items-center gap-1"
                      onClick={clearAll}
                      title="Clear all notifications"
                    >
                      <FaTrash size={9} /> Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Notification list */}
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-ink-muted">
                    <FaBell size={24} className="mb-2 text-ink-faint" />
                    <p className="text-sm font-medium">No notifications</p>
                    <p className="text-xs text-ink-faint">You're all caught up!</p>
                  </div>
                ) : (
                  notifications.map((n) => {
                    const iconDef = NOTIF_ICONS[n.type] || NOTIF_ICONS.system;
                    const Icon = iconDef.icon;
                    return (
                      <div
                        key={n.id}
                        className={`group flex items-start gap-3 px-4 py-3 border-b border-surface-border last:border-0 transition-colors ${
                          !n.read ? "bg-brand-50/40" : "hover:bg-surface-bg"
                        }`}
                      >
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-lg ${iconDef.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Icon size={13} className={iconDef.color} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-xs leading-snug ${!n.read ? "font-semibold text-ink-primary" : "text-ink-secondary"}`}>
                              {n.title}
                            </p>
                            {!n.read && (
                              <span className="w-2 h-2 rounded-full bg-brand flex-shrink-0 mt-1" />
                            )}
                          </div>
                          {n.message && (
                            <p className="text-2xs text-ink-muted mt-0.5 line-clamp-2">{n.message}</p>
                          )}
                          <p className="text-2xs text-ink-faint mt-1">{timeAgo(n.createdAt)}</p>
                        </div>

                        {/* Actions (visible on hover) */}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5">
                          {!n.read && (
                            <button
                              className="p-1 rounded hover:bg-surface-bg text-ink-faint hover:text-brand"
                              onClick={() => markAsRead(n.id)}
                              title="Mark as read"
                            >
                              <FaCheck size={10} />
                            </button>
                          )}
                          <button
                            className="p-1 rounded hover:bg-surface-bg text-ink-faint hover:text-red-500"
                            onClick={() => deleteNotif(n.id, !n.read)}
                            title="Delete"
                          >
                            <FaTrash size={9} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              {notifications.length > 0 && (
                <div className="px-4 py-2.5 border-t border-surface-border flex-shrink-0">
                  <button
                    className="w-full text-center text-xs text-brand hover:text-brand-700 font-medium"
                    onClick={() => { setNotifOpen(false); navigate("/settings#notifications"); }}
                  >
                    Notification Settings
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
